import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ButtonLink, Flex, Heading, Text } from '@indeed/ifl-components';
import { ArrowRight, Profile } from '@indeed/ifl-icons';
import { Edit } from '@indeed/ifl-icons';
import GettextFactory from '../../../util/GettextFactory';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import type { ContactInformationData } from './contactInfo';
import { SystemStyleObject } from '@indeed/ifl-css';

interface ContactInformationSectionProps {
    contactInfo: ContactInformationData;
}

export const ContactInformationSection: React.FC<ContactInformationSectionProps> = ({
    contactInfo,
}) => {
    const gt = GettextFactory();
    const location = useLocation();
    const { locale, countryCode, indeedProfileBaseUrl, origin } = useInitialState();
    const eventLogger = useEventLogger();
    const { fullName, emailAddress, phoneNumber, country, cityState, address } = contactInfo;

    const cityStateCountry = [cityState, country].filter(Boolean).join(', ');

    const editUrl = useMemo(() => {
        const url = new URL('/edit/contact', indeedProfileBaseUrl);
        url.searchParams.set('co', countryCode);
        url.searchParams.set('hl', locale);
        url.searchParams.set('from', 'auto-apply-preference');

        const continueUrl = new URL(`${origin}${location.pathname}${location.search}`);
        continueUrl.searchParams.set('from', 'profile-contact-edit');
        url.searchParams.set('continue', continueUrl.toString());

        return url.toString();
    }, [indeedProfileBaseUrl, countryCode, locale, origin, location.pathname, location.search]);

    return (
        <Flex sx={{ flexDirection: 'column', gap: 1 }}>
            <Flex
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Heading level={3} as="h2">
                    {gt.gettext('Contact information')}
                </Heading>
                <ButtonLink
                    href={editUrl}
                    aria-label={gt.gettext('Edit contact information')}
                    variant="tertiary"
                    iconOnly
                    data-testid="edit-contact-info-link"
                    onClick={() =>
                        eventLogger.logAutoApplySetupButtonClickEvent?.({
                            currentPage: location.pathname,
                            button: 'edit-contact-info',
                        })
                    }
                >
                    <Edit size="md" variant="stroke" aria-hidden />
                </ButtonLink>
            </Flex>
            <Flex
                sx={{
                    flexBasis: '100%',
                    border: '1px solid',
                    borderColor: 'border.weak',
                    borderRadius: 'md',
                    padding: 4,
                    gap: 2,
                }}
            >
                <Profile aria-hidden={true} />
                <Flex
                    sx={{
                        flexBasis: '100%',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'flex-start',
                    }}
                >
                    {fullName && (
                        <Text level={3} variant="contrast" sx={{ fontWeight: 'bold' }}>
                            {fullName}
                        </Text>
                    )}
                    {phoneNumber && (
                        <Text level={2} variant="contrast">
                            {phoneNumber}
                        </Text>
                    )}
                    {address && (
                        <Text level={2} variant="contrast">
                            {address}
                        </Text>
                    )}
                    {cityStateCountry && (
                        <Text level={2} variant="contrast">
                            {cityStateCountry}
                        </Text>
                    )}
                    {emailAddress && (
                        <Text level={2} variant="contrast">
                            {emailAddress}
                        </Text>
                    )}
                    {!phoneNumber && (
                        <ButtonLink
                            href={editUrl}
                            variant="secondary"
                            data-testid="add-phone-number-link"
                            sx={{ ...SecondaryDestructiveButtonLinkStyle }}
                            onClick={() =>
                                eventLogger.logAutoApplySetupButtonClickEvent?.({
                                    currentPage: location.pathname,
                                    button: 'add-phone-number',
                                })
                            }
                            iconAfter={<ArrowRight variant="stroke" aria-hidden={true} />}
                        >
                            {gt.gettext('Add phone number')}
                        </ButtonLink>
                    )}
                </Flex>
            </Flex>
        </Flex>
    );
};

const SecondaryDestructiveButtonLinkStyleDefault = {
    bg: 'background.action',
    borderColor: 'border.action.weak',
    color: 'foreground.action.destructive',
} satisfies SystemStyleObject;

export const SecondaryDestructiveButtonLinkStyle = {
    ...SecondaryDestructiveButtonLinkStyleDefault,
    '&:visited': {
        ...SecondaryDestructiveButtonLinkStyleDefault,
    },
    '&:hover:not([aria-disabled="true"])': {
        bg: 'background.action.hover',
        borderColor: 'border.action.weak.hover',
        color: 'foreground.action.destructive.hover',
    },
    '&:active:not([aria-disabled="true"]), &:focus-visible:not([aria-disabled="true"])': {
        bg: 'background.action.active',
        borderColor: 'border.action.weak.active',
        color: 'foreground.action.destructive.active',
    },
} satisfies SystemStyleObject;
